class MenuSearchBox extends HTMLElement {
  constructor() {
    super();
    this.debounceTimer = null;
    this.searchInput = null;
    this.menuItems = [];
    this.originalDisplay = new Map();
    this.originalTexts = new Map();
    this.isDrawerMenu = false;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Detectar se estamos em contexto de drawer mobile
    this.isDrawerMenu = !!this.closest(".menu-drawer, .menu-drawer__submenu");

    // Debug logging
    if (window.location.search.includes("debug=menu")) {
      console.log(
        "MenuSearchBox context:",
        this.isDrawerMenu ? "Mobile Drawer" : "Desktop Header"
      );
    }

    // Criar o input se não existir
    this.searchInput = this.querySelector("input") || this.createInput();

    // Encontrar todos os itens da lista que podem ser filtrados
    this.collectMenuItems();

    // Adicionar evento de busca
    this.searchInput.addEventListener("input", (e) => {
      this.debouncedSearch(e.target.value);
    });

    // Limpar busca ao focar
    this.searchInput.addEventListener("focus", () => {
      if (this.searchInput.value) {
        this.searchInput.select();
      }
    });
  }

  createInput() {
    const input = document.createElement("input");
    input.type = "text";
    input.className = this.isDrawerMenu
      ? "menu-drawer__search input--text caption-large"
      : "header__submenu-search input--text caption-large";
    input.placeholder = this.isDrawerMenu ? "Search menu..." : "Search...";
    input.setAttribute("aria-label", "Search menu items");
    this.appendChild(input);
    return input;
  }

  collectMenuItems() {
    // Encontrar a lista pai mais próxima
    const parentList = this.closest("ul");
    if (!parentList) return;

    // Coletar todos os <li> exceto o que contém este search-box
    this.menuItems = Array.from(parentList.children).filter((li) => {
      return (
        li !== this.closest("li") &&
        !li.classList.contains("search-box-item") &&
        !li.querySelector("menu-search-box")
      );
    });

    // Se não encontrou itens na lista direta, procurar em listas aninhadas (estrutura drawer)
    if (this.menuItems.length === 0) {
      const nestedList = parentList.querySelector(
        "ul.menu-drawer__menu, ul.list-menu"
      );
      if (nestedList) {
        this.menuItems = Array.from(nestedList.children).filter((li) => {
          return !li.querySelector("menu-search-box");
        });
      }
    }

    // Salvar o display original de cada item
    this.menuItems.forEach((item) => {
      this.originalDisplay.set(item, window.getComputedStyle(item).display);
    });
  }

  debouncedSearch(query) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.performSearch(query);
    }, 150);
  }

  performSearch(query) {
    const normalizedQuery = this.normalizeString(query);

    if (!normalizedQuery) {
      // Se a busca estiver vazia, mostrar todos os itens
      this.showAllItems();
      return;
    }

    let hasVisibleResults = false;

    this.menuItems.forEach((item) => {
      const itemHasMatches = this.filterItemAndSubItems(item, normalizedQuery);

      if (itemHasMatches) {
        this.showItem(item);
        // Aplicar highlight nos textos que fazem match
        this.highlightMatches(item, query);
        hasVisibleResults = true;
      } else {
        this.hideItem(item);
      }
    });

    // Mostrar mensagem se não houver resultados
    this.toggleNoResultsMessage(!hasVisibleResults, query);
  }

  calculateMatchScore(item, query) {
    const texts = this.extractSearchableText(item);
    let bestScore = 0;

    texts.forEach((text) => {
      const normalizedText = this.normalizeString(text);

      // Busca exata (peso máximo)
      if (normalizedText.includes(query)) {
        bestScore = Math.max(bestScore, 1.0);
        return;
      }

      // Busca fuzzy
      const fuzzyScore = this.fuzzyMatch(normalizedText, query);
      bestScore = Math.max(bestScore, fuzzyScore);

      // Busca por iniciais (ex: "ford mustang" -> "fm")
      const initialsScore = this.matchInitials(normalizedText, query);
      bestScore = Math.max(bestScore, initialsScore);
    });

    return bestScore;
  }

  filterItemAndSubItems(item, query) {
    // Verificar se o próprio item tem match
    const itemScore = this.calculateItemScore(item, query);
    let hasVisibleContent = itemScore > 0;
    let shouldExpand = itemScore >= 0.7;

    // Reset: remover todos os sub-itens ocultos anteriormente
    this.resetSubItemsVisibility(item);

    // Filtrar recursivamente todos os sub-itens
    const subItems = item.querySelectorAll("li");
    subItems.forEach((subItem) => {
      const subItemScore = this.calculateSubItemScore(subItem, query);

      if (subItemScore > 0) {
        this.showSubItem(subItem);
        hasVisibleContent = true;
        if (subItemScore >= 0.7) {
          shouldExpand = true;
        }
      } else {
        this.hideSubItem(subItem);
      }
    });

    // Expandir details se necessário
    if (shouldExpand && hasVisibleContent) {
      this.expandRelevantDetails(item, query);
    } else {
      this.collapseAllDetails(item);
    }

    return hasVisibleContent;
  }

  calculateItemScore(item, query) {
    // Calcular score para diferentes estruturas de menu
    let directSummary = item.querySelector(":scope > details > summary");

    // Para estrutura drawer mobile
    if (!directSummary) {
      directSummary = item.querySelector(
        ":scope > details.drawer-dropdown__category > summary"
      );
    }

    // Para links diretos (sem details)
    if (!directSummary) {
      const directLink = item.querySelector(":scope > a");
      if (directLink) {
        const text = this.normalizeString(directLink.textContent.trim());
        return this.calculateTextScore(text, query);
      }
      return 0;
    }

    const text = this.normalizeString(directSummary.textContent.trim());
    return this.calculateTextScore(text, query);
  }

  calculateSubItemScore(subItem, query) {
    // Calcular score para links e summaries do sub-item
    const texts = [];

    // Summary do sub-item (estrutura padrão)
    const summary = subItem.querySelector(":scope > details > summary");
    if (summary) {
      texts.push(summary.textContent.trim());
    }

    // Links diretos do sub-item (múltiplos seletores para diferentes estruturas)
    const directLinks = subItem.querySelectorAll(
      ":scope > a, :scope > details > div a, :scope > details > ul a, .drawer-dropdown__subcategory-link"
    );
    directLinks.forEach((link) => {
      texts.push(link.textContent.trim());
    });

    let bestScore = 0;
    texts.forEach((text) => {
      const normalizedText = this.normalizeString(text);
      const score = this.calculateTextScore(normalizedText, query);
      bestScore = Math.max(bestScore, score);
    });

    return bestScore;
  }

  calculateTextScore(normalizedText, query) {
    // Busca exata (peso máximo)
    if (normalizedText.includes(query)) {
      return 1.0;
    }

    // Busca fuzzy
    const fuzzyScore = this.fuzzyMatch(normalizedText, query);

    // Busca por iniciais
    const initialsScore = this.matchInitials(normalizedText, query);

    return Math.max(fuzzyScore, initialsScore);
  }

  showSubItem(subItem) {
    subItem.style.display = "";
    subItem.classList.remove("search-hidden");
  }

  hideSubItem(subItem) {
    subItem.style.display = "none";
    subItem.classList.add("search-hidden");
  }

  resetSubItemsVisibility(item) {
    const allSubItems = item.querySelectorAll("li");
    allSubItems.forEach((subItem) => {
      subItem.style.display = "";
      subItem.classList.remove("search-hidden");
    });
  }

  expandRelevantDetails(item, query) {
    // Expandir apenas details que contêm matches
    const allDetails = item.querySelectorAll("details");
    allDetails.forEach((details) => {
      const hasRelevantContent = this.detailsHasRelevantContent(details, query);
      if (hasRelevantContent) {
        details.setAttribute("open", "");

        // Para estrutura drawer, também adicionar classe de expansão se necessário
        if (details.classList.contains("drawer-dropdown__category")) {
          details.classList.add("drawer-dropdown__category--expanded");
        }
      }
    });
  }

  detailsHasRelevantContent(details, query) {
    // Verificar se o details ou seus filhos têm matches
    const summary = details.querySelector("summary");
    if (summary) {
      const summaryText = this.normalizeString(summary.textContent.trim());
      if (this.calculateTextScore(summaryText, query) > 0) {
        return true;
      }
    }

    // Verificar links dentro do details (múltiplos seletores)
    const links = details.querySelectorAll(
      "a, .drawer-dropdown__subcategory-link, .header__menu-item"
    );
    for (const link of links) {
      const linkText = this.normalizeString(link.textContent.trim());
      if (this.calculateTextScore(linkText, query) > 0) {
        return true;
      }
    }

    return false;
  }

  collapseAllDetails(item) {
    const allDetails = item.querySelectorAll("details[open]");
    allDetails.forEach((details) => {
      details.removeAttribute("open");

      // Remover classe de expansão do drawer se existir
      if (details.classList.contains("drawer-dropdown__category--expanded")) {
        details.classList.remove("drawer-dropdown__category--expanded");
      }
    });
  }

  extractSearchableText(item) {
    const texts = [];

    // Texto do summary principal (primeiro nível)
    const summary = item.querySelector("summary");
    if (summary) {
      const textContent = summary.textContent.trim();
      texts.push(textContent);
    }

    // Buscar todos os links recursivamente (múltiplos seletores para diferentes estruturas)
    const allLinks = item.querySelectorAll(
      "a, .drawer-dropdown__subcategory-link, .header__menu-item[href]"
    );
    allLinks.forEach((link) => {
      const linkText = link.textContent.trim();
      if (linkText.length > 0) {
        texts.push(linkText);
      }
    });

    // Buscar todos os summaries recursivamente (sub-details)
    const allSummaries = item.querySelectorAll("details summary");
    allSummaries.forEach((subSummary) => {
      const summaryText = subSummary.textContent.trim();
      if (summaryText.length > 0) {
        texts.push(summaryText);
      }
    });

    // Para estrutura drawer, buscar títulos específicos
    const drawerTitles = item.querySelectorAll(
      ".drawer-dropdown__category-title"
    );
    drawerTitles.forEach((title) => {
      const titleText = title.textContent.trim();
      if (titleText.length > 0) {
        texts.push(titleText);
      }
    });

    return texts.filter((text) => text.length > 0);
  }

  normalizeString(str) {
    return str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Remove acentos
      .replace(/[^\w\s]/g, "") // Remove pontuação
      .replace(/\s+/g, " ") // Normaliza espaços
      .trim();
  }

  fuzzyMatch(text, query) {
    if (query.length < 2) return 0;

    const words = query.split(" ").filter((word) => word.length > 1);
    let totalScore = 0;
    let matchedWords = 0;

    words.forEach((word) => {
      const wordScore = this.levenshteinSimilarity(text, word);
      if (wordScore > 0.6) {
        totalScore += wordScore;
        matchedWords++;
      }
    });

    return matchedWords > 0 ? (totalScore / words.length) * 0.8 : 0;
  }

  levenshteinSimilarity(s1, s2) {
    if (s1.includes(s2)) return 1.0;

    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;

    if (longer.length === 0) return 1.0;

    const distance = this.levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  }

  levenshteinDistance(s1, s2) {
    const matrix = Array(s2.length + 1)
      .fill(null)
      .map(() => Array(s1.length + 1).fill(null));

    for (let i = 0; i <= s1.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= s2.length; j++) matrix[j][0] = j;

    for (let j = 1; j <= s2.length; j++) {
      for (let i = 1; i <= s1.length; i++) {
        const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1,
          matrix[j - 1][i] + 1,
          matrix[j - 1][i - 1] + cost
        );
      }
    }

    return matrix[s2.length][s1.length];
  }

  matchInitials(text, query) {
    const words = text.split(" ");
    const initials = words
      .map((word) => word.charAt(0))
      .join("")
      .toLowerCase();

    if (initials.includes(query.toLowerCase())) {
      return 0.6;
    }

    return 0;
  }

  showItem(item) {
    const originalDisplay = this.originalDisplay.get(item) || "list-item";
    item.style.display = originalDisplay;
    item.classList.remove("search-hidden");
  }

  hideItem(item) {
    item.style.display = "none";
    item.classList.add("search-hidden");
  }

  expandAllDetails(item) {
    // Expandir todos os details dentro do item recursivamente
    const allDetails = item.querySelectorAll("details");
    allDetails.forEach((details) => {
      details.setAttribute("open", "");
    });
  }

  showAllItems() {
    this.menuItems.forEach((item) => {
      this.showItem(item);

      // Restaurar visibilidade de todos os sub-itens
      this.resetSubItemsVisibility(item);

      // Remover highlights
      this.removeHighlights(item);

      // Fechar todos os details que foram abertos pela busca
      const allDetails = item.querySelectorAll("details[open]");
      allDetails.forEach((details) => {
        details.removeAttribute("open");

        // Remover classes específicas do drawer
        if (details.classList.contains("drawer-dropdown__category--expanded")) {
          details.classList.remove("drawer-dropdown__category--expanded");
        }
      });
    });

    this.removeNoResultsMessage();
  }

  toggleNoResultsMessage(show, query) {
    this.removeNoResultsMessage();

    if (show) {
      const parentList = this.closest("ul");
      const noResultsItem = document.createElement("li");
      noResultsItem.className = "no-search-results";
      noResultsItem.style.cssText =
        "padding: 1rem; text-align: center; color: #666; font-style: italic;";
      noResultsItem.innerHTML = `No results found for "${query}"`;
      parentList.appendChild(noResultsItem);
    }
  }

  removeNoResultsMessage() {
    const existingMessage =
      this.closest("ul")?.querySelector(".no-search-results");
    if (existingMessage) {
      existingMessage.remove();
    }
  }

  // Método público para resetar a busca
  reset() {
    if (this.searchInput) {
      this.searchInput.value = "";
      this.showAllItems();
    }
  }

  // Método público para focar no input
  focus() {
    if (this.searchInput) {
      this.searchInput.focus();
    }
  }

  // Método específico para expandir categorias do drawer
  expandDrawerCategory(category, expand = true) {
    if (!this.isDrawerMenu) return;

    if (expand) {
      category.setAttribute("open", "");
      category.classList.add("drawer-dropdown__category--expanded");
    } else {
      category.removeAttribute("open");
      category.classList.remove("drawer-dropdown__category--expanded");
    }
  }
  // Highlight matches in text
  highlightMatches(item, query) {
    const normalizedQuery = this.normalizeString(query);
    if (!normalizedQuery) return;

    // Encontrar todos os elementos de texto relevantes
    const textElements = this.getTextElements(item);

    textElements.forEach((element) => {
      // Salvar texto original se ainda não foi salvo
      if (!this.originalTexts.has(element)) {
        this.originalTexts.set(element, element.innerHTML);
      }

      const originalText = element.textContent;
      const normalizedText = this.normalizeString(originalText);

      if (normalizedText.includes(normalizedQuery)) {
        const highlightedHTML = this.createHighlightedHTML(originalText, query);
        element.innerHTML = highlightedHTML;
      }
    });
  }

  // Remover highlights
  removeHighlights(item) {
    const textElements = this.getTextElements(item);

    textElements.forEach((element) => {
      if (this.originalTexts.has(element)) {
        element.innerHTML = this.originalTexts.get(element);
      }
    });
  }

  // Obter elementos de texto relevantes
  getTextElements(item) {
    const elements = [];

    // Elementos de texto diretos do item (summaries e links principais)
    const directSelectors = [
      ":scope > details > summary",
      ":scope > a",
      ":scope > details.drawer-dropdown__category > summary .drawer-dropdown__category-title",
    ];

    directSelectors.forEach((selector) => {
      const found = item.querySelectorAll(selector);
      found.forEach((el) => elements.push(el));
    });

    // Sub-elementos (links dentro de listas aninhadas)
    const subSelectors = [
      "a.drawer-dropdown__subcategory-link",
      "a.header__menu-item",
      "a.list-menu__item",
    ];

    subSelectors.forEach((selector) => {
      const found = item.querySelectorAll(selector);
      found.forEach((el) => {
        // Adicionar apenas se for um link com texto
        if (el.textContent.trim().length > 0) {
          elements.push(el);
        }
      });
    });

    // Remover duplicatas
    return [...new Set(elements)];
  }

  // Criar HTML com highlight
  createHighlightedHTML(text, query) {
    const normalizedQuery = this.normalizeString(query);
    const normalizedText = this.normalizeString(text);

    let result = text;

    // Encontrar todas as ocorrências (case insensitive)
    const regex = new RegExp(`(${this.escapeRegExp(query)})`, "gi");
    result = result.replace(
      regex,
      '<mark style="background-color: #ff4100; color: white; padding: 0.1em 0.2em; border-radius: 0.2em; font-weight: bold;">$1</mark>'
    );

    return result;
  }

  // Escape caracteres especiais do regex
  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }
  // Debug helper
  debugInfo() {
    return {
      isDrawerMenu: this.isDrawerMenu,
      menuItemsCount: this.menuItems.length,
      searchValue: this.searchInput?.value || "",
      parentContainer: this.closest("ul")?.className || "unknown",
    };
  }
}

customElements.define("menu-search-box", MenuSearchBox);
